/**
 * Mobile Cards Component
 * =====================
 * 
 * Mobile-optimized component for displaying form entries in a card-based layout.
 * This component provides a touch-friendly interface for viewing form submissions
 * on mobile devices, with simplified interaction patterns and optimized spacing.
 * 
 * Core Features:
 * - Card-based layout optimized for mobile screens
 * - Touch-friendly interaction with clickable cards
 * - Simplified data display without inline filtering
 * - Status field rendering with color-coded badges
 * - Date formatting optimized for mobile viewing
 * - Responsive design with proper mobile spacing
 * 
 * Design Philosophy:
 * - Eliminates complex table interactions for mobile
 * - Focuses on readability and touch accessibility
 * - Provides consistent card layout across all form types
 * - Integrates with advanced filtering modal for complex operations
 * 
 * Mobile Optimizations:
 * - Larger touch targets for better usability
 * - Simplified data presentation without sorting controls
 * - Card-based navigation for form entry viewing
 * - Consistent spacing and typography for mobile screens
 * 
 * Technical Implementation:
 * - Input-driven configuration matching table component
 * - Computed properties for responsive behavior
 * - Event emission for parent component handling
 * - Reusable formatting logic for data display
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, Output, EventEmitter, computed, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { TableColumn } from '../dynamic-table/dynamic-table.component';

@Component({
  selector: 'app-mobile-cards',
  templateUrl: './mobile-cards.component.html',
  styleUrls: ['./mobile-cards.component.css']
})
export class MobileCardsComponent implements OnChanges {
  @ViewChild('mobileCardsWrapper', { static: false }) mobileCardsWrapper!: ElementRef;
  
  @Input() loadingState: { isLoading: boolean; message: string } = { isLoading: false, message: '' };
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];


  @Output() action = new EventEmitter<any>();


  

  /**
   * Computed property to check if any columns are sortable.
   * Used to determine if sorting controls should be shown in the mobile interface.
   * Since mobile cards don't support inline sorting, this is primarily for
   * compatibility with the parent component's column configuration.
   * 
   * @returns True if any columns have sortable property set to true
   */
  hasSortableColumns = computed(() => {
    return this.columns.some(col => col.sortable);
  });


    /**
   * Lifecycle hook that responds to input property changes.
   * Handles data updates, form type changes, column changes, and sort state changes.
   * Manages filter state synchronization with sort column changes.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: any) {
    if (changes['loadingState']) {
      this.loadingState = changes['loadingState'].currentValue;
    }
  }
 

  /**
   * Handles card click events for navigation.
   * Emits the row data to the parent component for further processing,
   * typically navigation to the form view component.
   * 
   * @param row - The form entry data that was clicked
   */
  onAction(row: any) {
    this.action.emit(row);
  }



  /**
   * Gets the appropriate color for status badges.
   * Maps status values to predefined Ionic colors for consistent styling
   * across the mobile interface. Provides visual hierarchy for status information.
   * 
   * @param status - The status value to get color for
   * @returns String color name for the status badge
   */
  getStatusColor(status: string): string {
    const statusMap: Record<string, string> = {
      'completed': 'success',
      'pending': 'warning',
      'rejected': 'danger',
      'draft': 'medium'
    };
    return statusMap[status.toLowerCase()] || 'medium';
  }

  /**
   * Scrolls to the top of the mobile cards wrapper.
   * Provides smooth scrolling behavior for better user experience
   * when navigating back to the top of long lists.
   */
  scrollToTop(): void {
    if (this.mobileCardsWrapper) {
      this.mobileCardsWrapper.nativeElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  /**
   * Formats cell values for display in mobile cards.
   * Handles different data types with appropriate formatting:
   * - Dates are converted to localized date strings
   * - Other values are displayed as-is with fallback to empty string
   * 
   * @param row - The row data containing the value to format
   * @param column - The column configuration defining the data type
   * @returns Formatted string representation of the value
   */
  formatValue(row: any, column: TableColumn): string {
    const value = row[column.key];
    
    if (column.type === 'date' && value) {
      return new Date(value).toLocaleDateString();
    }
    
    return value || '';
  }
}